import { useCallback, useLayoutEffect, useRef, useState } from 'react';

export type UseCSSVariableOptions = {
  /** CSS variable name (e.g., '--sidebar-width') */
  name: string;
  /** Initial value in pixels */
  initialValue: number;
  /** localStorage key for persistence (optional) */
  localStorageKey?: string;
  /** Synchronizes width from an external source (e.g. layout transitions) before paint. */
  externalValue?: number;
};

/**
 * Hook to manage a CSS variable on the document root.
 * Abstracts direct document manipulation and optionally persists to localStorage.
 */
export const useCSSVariable = ({ name, initialValue, localStorageKey, externalValue }: UseCSSVariableOptions) => {
  const [value, setValue] = useState<number>(() => {
    if (localStorageKey && typeof window !== 'undefined') {
      const stored = window.localStorage.getItem(localStorageKey);

      if (stored) {
        const parsed = parseInt(stored, 10);

        if (!Number.isNaN(parsed)) {
          return parsed;
        }
      }
    }

    return initialValue;
  });

  // Track previous externalValue so we only sync on actual changes, not on remount.
  // On remount the ref re-initializes to the current externalValue, making the comparison
  // a no-op — this preserves the localStorage-initialized value instead of overriding it
  // with a stale externalValue that was set during a previous layout transition.
  const prevExternalValueRef = useRef(externalValue);
  useLayoutEffect(() => {
    if (externalValue !== undefined && externalValue !== prevExternalValueRef.current) {
      setValue(externalValue);
    }
    prevExternalValueRef.current = externalValue;
  }, [externalValue]);

  // Sync CSS variable to document on mount and value change
  useLayoutEffect(() => {
    document.documentElement.style.setProperty(name, `${value}px`);
  }, [name, value]);

  const setCSSVariable = useCallback(
    (newValue: number) => {
      setValue(newValue);
      if (localStorageKey) {
        window.localStorage.setItem(localStorageKey, newValue.toString());
      }
    },
    [localStorageKey],
  );

  return [value, setCSSVariable] as const;
};
